import { useRef, useState } from "react";
import { orderConfirmationGateway } from "../../infrastructure/api/order-confirmation-gateway";
import { OrderConfirmationError } from "../../infrastructure/api/order-confirmation-contracts";
import type { OrderConfirmationInput, OrderConfirmationWorkspace } from "../../domain/order-confirmation";
type Scope = { isCurrent: () => boolean; };
type Props = {
  scope: Scope;
  workspace: OrderConfirmationWorkspace | null;
  input: OrderConfirmationInput;
  valid: boolean;
  invalidate: () => void;
};
export function useOrderConfirmationResponse({
  scope,
  workspace,
  input,
  valid,
  invalidate
}: Props) {
  const orderId = workspace?.order.id ?? null;
  const [state, setState] = useState({
    scope,
    orderId,
    submitting: false,
    done: false,
    error: "",
    uncertain: false
  });
  const contextRef = useRef({ scope, orderId, busy: false, attempt: null as { fingerprint: string; key: string; input: OrderConfirmationInput; } | null });
  if (contextRef.current.scope !== scope || contextRef.current.orderId !== orderId) contextRef.current = { scope, orderId, busy: false, attempt: null };
  const context = contextRef.current;
  const active = useRef({ scope, workspace }); active.current = { scope, workspace };
  const current = () => scope.isCurrent() && contextRef.current === context && active.current.scope === scope && active.current.workspace?.order.id === workspace?.order.id;
  const known = state.scope === scope && state.orderId === orderId ? state : {
    scope,
    orderId,
    submitting: false,
    done: false,
    error: "",
    uncertain: false
  };
  const submit = async () => {
    if (!workspace || !current() || context.busy || known.done || (!known.uncertain && !valid)) return;
    const fingerprint = JSON.stringify(input);
    if (!known.uncertain && context.attempt?.fingerprint !== fingerprint) context.attempt = { fingerprint, key: crypto.randomUUID(), input };
    const attempt = context.attempt;
    if (!attempt) return;
    context.busy = true;
    setState({
      scope,
      orderId,
      submitting: true,
      done: false,
      error: "",
      uncertain: known.uncertain
    });
    try {
      await orderConfirmationGateway.respond(attempt.input, attempt.key);
      if (!current()) return;
      invalidate();
      setState({
        scope,
        orderId,
        submitting: false,
        done: true,
        error: "",
        uncertain: false
      });
    } catch (caught) {
      if (!current()) return;
      const uncertain = known.uncertain || !(caught instanceof OrderConfirmationError) || caught.uncertain;
      const message = uncertain ? "Yanıtın kaydedildiği doğrulanamadı. Aynı yanıtı tekrar gönderin." : caught.message;
      setState({
        scope,
        orderId,
        submitting: false,
        done: false,
        error: message,
        uncertain
      });
    } finally { context.busy = false; }
  };
  const clearError = () => {
    if (scope.isCurrent() && !context.busy && !known.uncertain) setState({
      scope,
      orderId,
      submitting: false,
      done: known.done,
      error: "",
      uncertain: false
    });
  };
  return { ...known, submit, clearError };
}
